import { useEffect, useRef } from 'react';

export interface TicketNotificationPayload {
  ticketNumber: string;
  subject: string;
  novoStatus: string;
  statusAnterior?: string;
  mensagemSuporte?: string;
}

/**
 * Dispara um evento global quando um chamado é criado ou atualizado.
 * Outros componentes (ex: ParticipanteLayout) escutam esse evento para
 * adicionar notificações no sino.
 */
export function dispararNotificacaoChamado(payload: TicketNotificationPayload) {
  const event = new CustomEvent('ticketAtualizado', { detail: payload });
  window.dispatchEvent(event);
}

const STATUS_LABELS: Record<string, string> = {
  aberto: 'Aberto',
  'em-andamento': 'Em Andamento',
  aguardando: 'Aguardando sua resposta',
  resolvido: 'Resolvido',
  fechado: 'Fechado',
};

const STATUS_ICONS: Record<string, string> = {
  aberto: 'ri-ticket-line',
  'em-andamento': 'ri-loader-4-line',
  aguardando: 'ri-question-answer-line',
  resolvido: 'ri-checkbox-circle-line',
  fechado: 'ri-lock-line',
};

/**
 * Retorna ícone e label formatados para um status de chamado.
 */
export function getTicketStatusInfo(status: string) {
  return {
    label: STATUS_LABELS[status] ?? status,
    icon: STATUS_ICONS[status] ?? 'ri-ticket-line',
  };
}

/**
 * Hook que simula atualizações automáticas de status de chamados abertos.
 * Em produção, isso seria substituído por polling/websocket da API.
 *
 * @param onUpdate - callback chamado quando há atualização de status
 * @param enabled  - ativa/desativa o polling (default: true)
 */
export function useTicketStatusPolling(
  onUpdate: (payload: TicketNotificationPayload) => void,
  enabled = true,
) {
  const callbackRef = useRef(onUpdate);
  callbackRef.current = onUpdate;

  useEffect(() => {
    if (!enabled) return;

    // Simula respostas do suporte em intervalos realistas
    const simulacoes: Array<{ delay: number; payload: TicketNotificationPayload }> = [
      {
        delay: 25000, // 25s — suporte recebe e coloca em andamento
        payload: {
          ticketNumber: 'TK123456',
          subject: 'Erro ao carregar dashboard de pontos',
          novoStatus: 'em-andamento',
          statusAnterior: 'aberto',
          mensagemSuporte: 'Olá! Já recebemos seu chamado e nossa equipe está analisando o problema.',
        },
      },
      {
        delay: 55000, // 55s — suporte pede mais informações
        payload: {
          ticketNumber: 'TK123456',
          subject: 'Erro ao carregar dashboard de pontos',
          novoStatus: 'aguardando',
          statusAnterior: 'em-andamento',
          mensagemSuporte: 'Precisamos de mais detalhes. Qual navegador você está usando?',
        },
      },
      {
        delay: 90000, // 90s — chamado resolvido
        payload: {
          ticketNumber: 'TK123456',
          subject: 'Erro ao carregar dashboard de pontos',
          novoStatus: 'resolvido',
          statusAnterior: 'aguardando',
          mensagemSuporte: 'Problema identificado e corrigido! Por favor, confirme se está funcionando.',
        },
      },
    ];

    const timers = simulacoes.map(({ delay, payload }) =>
      window.setTimeout(() => callbackRef.current(payload), delay),
    );

    return () => timers.forEach(clearTimeout);
  }, [enabled]);
}
